/* Integrations Center — /crm/integraciones
 *
 * Grid of provider cards + connection detail modal + sync history.
 * FASE 4B.1: adapters are stubs returning "not implemented" — the UI wiring,
 * status badges, sync history, and disconnect flows are all functional.
 * FASE 4B.2 will replace the stubs with real API calls; NO UI changes needed.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Zap, Loader2, CheckCircle2, AlertCircle, XCircle, Clock,
  RefreshCw, Unplug, Key, Copy, Trash2, Plus, ExternalLink,
  FileSpreadsheet, Sparkles, Rocket, User, Bot,
} from 'lucide-react';
import { api, friendlyError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const PROVIDER_ICON = {
  wasi:       { emoji: '🏠', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  tokko:      { emoji: '🔷', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  inmovilla:  { emoji: '🇪🇸', color: 'bg-red-50 border-red-200 text-red-700' },
  hubspot:    { emoji: '🧡', color: 'bg-orange-50 border-orange-200 text-orange-700' },
  salesforce: { emoji: '☁️', color: 'bg-sky-50 border-sky-200 text-sky-700' },
  zoho:       { emoji: '🟣', color: 'bg-purple-50 border-purple-200 text-purple-700' },
};

function StatusPill({ status }) {
  const map = {
    connected:       { icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Conectado' },
    ok:              { icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Operativo' },
    pending:         { icon: Clock,        cls: 'text-amber-700 bg-amber-50 border-amber-200',       label: 'Pendiente' },
    partial:         { icon: AlertCircle,  cls: 'text-orange-700 bg-orange-50 border-orange-200',    label: 'Parcial' },
    error:           { icon: XCircle,      cls: 'text-rose-700 bg-rose-50 border-rose-200',          label: 'Error' },
    not_implemented: { icon: Clock,        cls: 'text-gray-600 bg-gray-100 border-gray-200',         label: 'Próximamente (FASE 4B.2)' },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

// ─────────────────────────────────────── Provider-specific credential fields

/** Returns the input fields to render for each provider's connection form. */
function credentialFieldsFor(providerSlug) {
  if (providerSlug === 'wasi') {
    return [
      { key: 'id_company', label: 'ID de tu empresa (id_company)', placeholder: 'Ej: 1234567890', type: 'text' },
      { key: 'wasi_token', label: 'Token Wasi (wasi_token)', placeholder: 'ABCD_EFGH_IJKL_MNOP', type: 'password' },
    ];
  }
  if (providerSlug === 'tokko') {
    return [{ key: 'api_key', label: 'API Key Tokko', placeholder: '', type: 'password' }];
  }
  if (providerSlug === 'inmovilla') {
    return [{ key: 'api_key', label: 'API Key Inmovilla', placeholder: '', type: 'password' }];
  }
  // Fallback for future providers
  return [
    { key: 'api_token', label: `API token de ${providerSlug}`, placeholder: '', type: 'password' },
    { key: 'api_url',   label: 'URL base de la API (opcional)', placeholder: '', type: 'text' },
  ];
}

const SCHEDULE_OPTIONS = [
  { value: 'manual',  label: 'Solo manual' },
  { value: 'hourly',  label: 'Cada hora' },
  { value: '6h',      label: 'Cada 6 horas' },
  { value: '12h',     label: 'Cada 12 horas' },
  { value: 'daily',   label: 'Diaria (3:00 AM)' },
];

// ─────────────────────────────────────── Connect modal

function ConnectModal({ provider, onClose, onConnected }) {
  const { toast } = useToast();
  const fields = credentialFieldsFor(provider?.slug);
  const [credentials, setCredentials] = useState(() =>
    Object.fromEntries(fields.map(f => [f.key, ''])),
  );
  const [schedule, setSchedule] = useState('daily');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);   // rich summary from /test-sync
  const [saving, setSaving] = useState(false);
  if (!provider) return null;

  const hasRealAdapter = ['wasi'].includes(provider.slug);
  const allFieldsFilled = fields.every(f => (credentials[f.key] || '').trim().length > 0);

  const verify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    setTestResult(null);
    try {
      const res = await api.integrationsVerify(provider.slug, credentials);
      setVerifyResult(res);
      if (res.ok) toast({ title: 'Conexión exitosa', description: res.message });
      else toast({ title: `${provider.display_name} rechazó las credenciales`, description: res.message, variant: 'destructive' });
    } catch (err) {
      const msg = friendlyError(err);
      setVerifyResult({ ok: false, message: msg });
      toast({ title: 'No pudimos verificar', description: msg, variant: 'destructive' });
    } finally { setVerifying(false); }
  };

  const testSync = async () => {
    // Persist connection with schedule=manual first, then test-sync, so the
    // real code path is exercised. If the user cancels, they can disconnect.
    setTesting(true);
    setTestResult(null);
    try {
      await api.integrationsConnect({
        provider: provider.slug,
        credentials,
        sync_schedule: 'manual',
      });
      const res = await api.integrationsTestSync(provider.slug);
      setTestResult(res);
      toast({
        title: res.ok ? 'Prueba exitosa' : 'Prueba con errores',
        description: `${res.new} nuevas · ${res.updated} actualizadas · ${res.errors} con errores`,
      });
    } catch (err) {
      toast({ title: 'Error en prueba', description: friendlyError(err), variant: 'destructive' });
    } finally { setTesting(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.integrationsConnect({
        provider: provider.slug,
        credentials,
        sync_schedule: schedule,
      });
      toast({
        title: res.connect_result.status === 'ok' ? 'Integración activada' : 'Guardada como pendiente',
        description: res.connect_result.status === 'ok'
          ? (schedule === 'manual' ? 'Sincronización manual habilitada.' : `Se sincronizará automáticamente (${SCHEDULE_OPTIONS.find(o => o.value === schedule)?.label}).`)
          : (res.connect_result.errors?.[0] || 'Revisa las credenciales'),
      });
      onConnected();
      onClose();
    } catch (err) {
      toast({ title: 'No pudimos guardar', description: friendlyError(err), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const canSubmit = allFieldsFilled && (!hasRealAdapter || verifyResult?.ok);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="integrations-connect-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl">{PROVIDER_ICON[provider.slug]?.emoji || '🔌'}</span>
          <div className="flex-1">
            <h3 className="font-serif text-xl font-bold text-habita-dark">Conectar {provider.display_name}</h3>
            <p className="text-xs text-gray-500">
              {hasRealAdapter
                ? 'Verifica tus credenciales, elige la frecuencia y activa la sincronización.'
                : 'Guarda tus credenciales para más adelante. Los adapters reales llegan pronto.'}
            </p>
          </div>
        </div>

        {!hasRealAdapter && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-amber-800" data-testid="stub-adapter-notice">
            <strong>Nota:</strong> este adapter aún no está implementado (FASE 4B.3). Por ahora la conexión queda como pendiente.
          </div>
        )}

        {provider.slug === 'wasi' && (
          <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-emerald-800" data-testid="wasi-help">
            <p className="mb-1">🔑 <strong>¿Dónde encuentro mis credenciales?</strong></p>
            <p>En Wasi: <em>Configuración → Ajustes generales → API Wasi → Generar token</em>. Necesitas el plan Pro.</p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          {fields.map(f => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
              <input
                type={f.type}
                placeholder={f.placeholder}
                value={credentials[f.key] || ''}
                onChange={e => { setCredentials(c => ({ ...c, [f.key]: e.target.value })); setVerifyResult(null); }}
                className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm"
                data-testid={`credentials-${f.key}`}
                autoComplete="off"
              />
            </div>
          ))}

          {hasRealAdapter && (
            <>
              <button
                type="button"
                onClick={verify}
                disabled={verifying || !allFieldsFilled}
                className={`w-full h-11 rounded-full font-semibold border flex items-center justify-center gap-2 transition-colors ${
                  verifyResult?.ok
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                    : verifyResult && !verifyResult.ok
                    ? 'bg-rose-50 text-rose-700 border-rose-300'
                    : 'bg-white text-habita border-habita hover:bg-habita/5'
                } disabled:opacity-40`}
                data-testid="verify-connection-btn"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" />
                  : verifyResult?.ok ? <CheckCircle2 className="w-4 h-4" />
                  : verifyResult && !verifyResult.ok ? <XCircle className="w-4 h-4" />
                  : <Zap className="w-4 h-4" />}
                {verifying ? 'Verificando…'
                  : verifyResult?.ok ? 'Conexión exitosa'
                  : verifyResult && !verifyResult.ok ? 'Reintentar verificación'
                  : 'Verificar conexión'}
              </button>
              {verifyResult && !verifyResult.ok && (
                <p className="text-xs text-rose-600 -mt-1" data-testid="verify-error-message">{verifyResult.message}</p>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Frecuencia de sincronización</label>
                <select
                  value={schedule}
                  onChange={e => setSchedule(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-habita outline-none text-sm bg-white"
                  data-testid="sync-schedule-select"
                >
                  {SCHEDULE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">Puedes cambiarla en cualquier momento desde el Centro.</p>
              </div>

              {verifyResult?.ok && (
                <button
                  type="button"
                  onClick={testSync}
                  disabled={testing}
                  className="w-full h-11 rounded-full font-semibold border bg-white text-habita border-habita hover:bg-habita/5 disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="test-sync-btn"
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  {testing ? 'Probando…' : 'Probar sincronización'}
                </button>
              )}

              {testResult && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs" data-testid="test-sync-result">
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.ok
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      : <AlertCircle className="w-4 h-4 text-amber-600" />}
                    <p className="font-semibold text-gray-800">
                      {testResult.ok ? 'Prueba exitosa' : 'Prueba completada con hallazgos'}
                    </p>
                    <span className="ml-auto text-[10px] text-gray-400">{(testResult.duration_ms / 1000).toFixed(1)}s</span>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 text-center">
                    <div><p className="text-[9px] text-gray-500 uppercase">Encontrado</p><p className="text-sm font-bold text-gray-900 tabular-nums" data-testid="test-found">{testResult.found}</p></div>
                    <div><p className="text-[9px] text-emerald-600 uppercase">Nuevas</p><p className="text-sm font-bold text-emerald-700 tabular-nums" data-testid="test-new">{testResult.new}</p></div>
                    <div><p className="text-[9px] text-amber-600 uppercase">Update</p><p className="text-sm font-bold text-amber-700 tabular-nums" data-testid="test-updated">{testResult.updated}</p></div>
                    <div><p className="text-[9px] text-rose-600 uppercase">Errores</p><p className="text-sm font-bold text-rose-700 tabular-nums" data-testid="test-errors">{testResult.errors}</p></div>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2">Ejecutado: {new Date(testResult.ran_at).toLocaleString('es-CO')}</p>
                </div>
              )}
            </>
          )}

          <button
            type="submit"
            disabled={saving || !canSubmit}
            className="w-full h-11 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="integrations-connect-submit"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {hasRealAdapter ? 'Activar sincronización' : 'Guardar conexión'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── API Keys panel

function ApiKeysPanel() {
  const { toast } = useToast();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [rawKey, setRawKey] = useState(null);

  const load = async () => {
    try {
      const r = await api.apiKeysList();
      setKeys(r.keys || []);
    } catch (err) { toast({ title: 'No pudimos cargar', description: friendlyError(err), variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const r = await api.apiKeysCreate({ name: newKeyName.trim(), scopes: ['properties:write', 'leads:write', 'projects:write'] });
      setRawKey(r);
      setNewKeyName('');
      await load();
    } catch (err) { toast({ title: 'No pudimos crear', description: friendlyError(err), variant: 'destructive' }); }
    finally { setCreating(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm('¿Revocar esta llave? Los sistemas conectados dejarán de funcionar.')) return;
    try {
      await api.apiKeysRevoke(id);
      toast({ title: 'Llave revocada' });
      await load();
    } catch (err) { toast({ title: 'Error', description: friendlyError(err), variant: 'destructive' }); }
  };
  const copy = (text) => { navigator.clipboard?.writeText(text); toast({ title: 'Copiado al portapapeles' }); };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5" data-testid="api-keys-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2"><Key className="w-4 h-4" /> API Keys</h3>
          <p className="text-xs text-gray-500 mt-1">Autoriza sistemas externos a enviar propiedades y leads a Habita. <a href="/docs" target="_blank" rel="noopener noreferrer" className="text-habita hover:underline inline-flex items-center gap-1">Ver docs <ExternalLink className="w-3 h-3" /></a></p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            placeholder="Nombre (ej: Mi CRM Wasi)"
            className="h-9 px-3 rounded-full border border-gray-200 text-sm focus:border-habita outline-none"
            data-testid="apikey-name-input"
          />
          <button onClick={create} disabled={creating || !newKeyName.trim()} className="h-9 px-3 rounded-full bg-habita text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-60" data-testid="apikey-create-btn">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Crear
          </button>
        </div>
      </div>

      {rawKey && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="apikey-raw-display">
          <p className="text-xs font-semibold text-emerald-800 mb-1">✅ Copia tu llave — solo se mostrará una vez</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs font-mono bg-white p-2 rounded border border-emerald-200 break-all">{rawKey.raw_key}</code>
            <button onClick={() => copy(rawKey.raw_key)} className="p-2 rounded-lg bg-white border border-emerald-200 hover:bg-emerald-100"><Copy className="w-4 h-4 text-emerald-700" /></button>
          </div>
          <button onClick={() => setRawKey(null)} className="text-xs text-emerald-700 hover:underline mt-2">Ya copié — ocultar</button>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-gray-400 py-4 text-center"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></p>
      ) : keys.length === 0 ? (
        <p className="text-xs text-gray-400 py-6 text-center italic">Sin llaves. Crea la primera arriba ↑</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {keys.map(k => (
            <li key={k.id} className="flex items-center justify-between py-2.5" data-testid={`apikey-row-${k.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{k.name}</p>
                <p className="text-xs text-gray-500"><code className="font-mono">{k.prefix}…</code> · {k.scopes.join(', ')} · Usos: {k.usage_count}</p>
              </div>
              <button onClick={() => revoke(k.id)} className="text-rose-600 hover:text-rose-800 p-1.5" title="Revocar"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────── Provider card

function ProviderCard({ provider, connection, onConnect, onSync, onDisconnect, onScheduleChange }) {
  const icon = PROVIDER_ICON[provider.slug] || { emoji: '🔌', color: 'bg-gray-50 border-gray-200 text-gray-700' };
  const isConnected = !!connection;
  const [busy, setBusy] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const doSync = async () => { setBusy(true); try { await onSync(provider.slug); } finally { setBusy(false); } };
  const doDisc = async () => {
    if (!window.confirm(`¿Desconectar ${provider.display_name}? La historia de sync se conserva.`)) return;
    setBusy(true); try { await onDisconnect(provider.slug); } finally { setBusy(false); }
  };
  const doScheduleChange = async (val) => {
    setSavingSchedule(true);
    try { await onScheduleChange(provider.slug, val); }
    finally { setSavingSchedule(false); }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 hover:border-habita/40 transition-colors" data-testid={`provider-card-${provider.slug}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl border ${icon.color}`}>{icon.emoji}</div>
          <div>
            <p className="font-semibold text-gray-900">{provider.display_name}</p>
            <p className="text-[11px] text-gray-500 uppercase tracking-wider mt-0.5">{provider.sync_direction}</p>
          </div>
        </div>
        {provider.coming_soon ? (
          <StatusPill status="not_implemented" />
        ) : (
          <StatusPill status={isConnected ? (connection.last_sync_status || 'connected') : 'pending'} />
        )}
      </div>

      {isConnected && (
        <div className="space-y-2 mb-3 text-xs" data-testid={`connection-status-${provider.slug}`}>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-gray-500 text-[10px] uppercase tracking-widest">Última sync</p>
              <p className="text-gray-900 font-medium">{connection.last_sync_at ? new Date(connection.last_sync_at).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Sin sincronizaciones aún'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-gray-500 text-[10px] uppercase tracking-widest">Próxima</p>
              <p className="text-gray-900 font-medium">{connection.next_sync_at ? new Date(connection.next_sync_at).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            <div className="bg-emerald-50 rounded-lg p-1.5 text-center">
              <p className="text-emerald-600 text-[9px] uppercase font-semibold tracking-wider">Import</p>
              <p className="text-emerald-800 font-bold tabular-nums text-sm">{connection.last_sync_imported ?? 0}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-1.5 text-center">
              <p className="text-amber-600 text-[9px] uppercase font-semibold tracking-wider">Update</p>
              <p className="text-amber-800 font-bold tabular-nums text-sm">{connection.last_sync_updated ?? 0}</p>
            </div>
            <div className="bg-rose-50 rounded-lg p-1.5 text-center">
              <p className="text-rose-600 text-[9px] uppercase font-semibold tracking-wider">Error</p>
              <p className="text-rose-800 font-bold tabular-nums text-sm">{connection.last_sync_failed ?? 0}</p>
            </div>
            <div className="bg-gray-100 rounded-lg p-1.5 text-center">
              <p className="text-gray-600 text-[9px] uppercase font-semibold tracking-wider">Duración</p>
              <p className="text-gray-800 font-bold tabular-nums text-sm">{connection.last_sync_duration_ms ? `${(connection.last_sync_duration_ms / 1000).toFixed(1)}s` : '—'}</p>
            </div>
          </div>
          {(connection.last_sync_errors?.length || 0) > 0 && (
            <div className="bg-rose-50 border border-rose-100 rounded-lg p-2 text-[10px] text-rose-700" data-testid={`last-sync-errors-${provider.slug}`}>
              <strong>Error:</strong> {connection.last_sync_errors[0]}
            </div>
          )}
        </div>
      )}

      {isConnected && !provider.coming_soon && (
        <div className="mb-3">
          <label className="text-[10px] text-gray-500 uppercase tracking-widest">Frecuencia</label>
          <div className="flex items-center gap-1.5 mt-1">
            <select
              value={connection.sync_schedule || 'manual'}
              onChange={e => doScheduleChange(e.target.value)}
              disabled={savingSchedule}
              className="flex-1 h-8 text-xs px-2 rounded-lg border border-gray-200 focus:border-habita outline-none bg-white disabled:opacity-60"
              data-testid={`schedule-select-${provider.slug}`}
            >
              {SCHEDULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {savingSchedule && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        {provider.coming_soon ? (
          <button disabled className="flex-1 h-9 rounded-full bg-gray-100 text-gray-400 text-xs font-semibold cursor-not-allowed">
            Próximamente
          </button>
        ) : !isConnected ? (
          <button onClick={() => onConnect(provider)} className="flex-1 h-9 rounded-full bg-habita text-white text-xs font-semibold hover:bg-habita-dark" data-testid={`connect-${provider.slug}`}>
            Conectar
          </button>
        ) : (
          <>
            <button onClick={doSync} disabled={busy} className="flex-1 h-9 rounded-full bg-habita text-white text-xs font-semibold hover:bg-habita-dark disabled:opacity-60 flex items-center justify-center gap-1" data-testid={`sync-${provider.slug}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sincronizar
            </button>
            <button onClick={doDisc} disabled={busy} className="h-9 px-3 rounded-full bg-white border border-gray-200 text-gray-700 hover:border-rose-300 hover:text-rose-700 text-xs font-semibold flex items-center gap-1" data-testid={`disconnect-${provider.slug}`}>
              <Unplug className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Sync history

function SyncHistory({ provider, agencyReady }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = () => {
    if (!provider || !agencyReady) return;
    setLoading(true);
    api.integrationsLogs(provider).then(r => setLogs(r.logs || [])).catch(() => setLogs([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [provider, agencyReady]);    
  if (!provider) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5" data-testid="sync-history">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 text-sm">Historial · {provider}</h3>
        <button onClick={load} className="text-xs text-habita hover:text-habita-dark flex items-center gap-1" data-testid="sync-history-refresh">
          <RefreshCw className="w-3 h-3" /> Actualizar
        </button>
      </div>
      {loading ? <Loader2 className="w-4 h-4 animate-spin text-gray-400 mx-auto" /> : logs.length === 0 ? (
        <p className="text-xs text-gray-400 italic text-center py-4">Sin sincronizaciones aún.</p>
      ) : (
        <ul className="space-y-2 max-h-[380px] overflow-y-auto" data-testid="sync-history-list">
          {logs.map(l => {
            const isAuto = l.triggered_by === 'scheduled';
            const isTest = l.triggered_by === 'test';
            const trigLabel = isAuto ? 'Auto' : isTest ? 'Prueba' : 'Manual';
            const trigCls = isAuto ? 'bg-sky-50 text-sky-700 border-sky-200'
              : isTest ? 'bg-violet-50 text-violet-700 border-violet-200'
              : 'bg-gray-100 text-gray-700 border-gray-200';
            return (
              <li key={l.id} className="border border-gray-100 rounded-lg p-2.5 text-xs" data-testid={`sync-log-${l.id}`}>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <StatusPill status={l.status} />
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold uppercase tracking-wider ${trigCls}`}>
                    {isAuto ? <Bot className="w-2.5 h-2.5 inline mr-0.5" /> : <User className="w-2.5 h-2.5 inline mr-0.5" />}
                    {trigLabel}
                  </span>
                  <span className="text-gray-700 font-medium capitalize">{l.entity}</span>
                  <span className="text-gray-400 ml-auto">{new Date(l.created_at).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className="grid grid-cols-4 gap-1 text-center">
                  <div><p className="text-[9px] text-emerald-600">Import</p><p className="font-bold tabular-nums">{l.imported ?? 0}</p></div>
                  <div><p className="text-[9px] text-amber-600">Update</p><p className="font-bold tabular-nums">{l.updated ?? 0}</p></div>
                  <div><p className="text-[9px] text-gray-500">Omitido</p><p className="font-bold tabular-nums">{l.skipped ?? 0}</p></div>
                  <div><p className="text-[9px] text-rose-600">Error</p><p className="font-bold tabular-nums">{l.failed ?? 0}</p></div>
                </div>
                <div className="flex items-center justify-between mt-1.5 text-[10px] text-gray-500">
                  <span>Por: {l.user_name || 'Sistema'}</span>
                  <span>{l.duration_ms ? `${(l.duration_ms / 1000).toFixed(2)}s` : '—'}</span>
                </div>
                {(l.errors?.length || 0) > 0 && (
                  <details className="mt-1.5">
                    <summary className="text-[10px] text-rose-600 cursor-pointer">Ver {l.errors.length} error(es)</summary>
                    <ul className="mt-1 space-y-0.5 pl-2">
                      {l.errors.slice(0, 5).map((e, i) => <li key={i} className="text-[10px] text-rose-700">• {e}</li>)}
                    </ul>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────── Main

export default function IntegrationsCenter() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([api.integrationsProviders(), api.integrationsConnections()]);
      setProviders(p.providers || []);
      setConnections(c.connections || []);
    } catch (err) { toast({ title: 'No pudimos cargar', description: friendlyError(err), variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byProvider = useMemo(() => {
    const m = {};
    connections.forEach(c => { m[c.provider] = c; });
    return m;
  }, [connections]);

  // Auto-select the first connected provider so the history panel is visible
  // immediately on page load. User can still switch via onSync.
  useEffect(() => {
    if (!selectedProvider && connections.length > 0) {
      setSelectedProvider(connections[0].provider);
    }
  }, [connections, selectedProvider]);

  const onSync = async (slug) => {
    try {
      const r = await api.integrationsSync(slug);
      const logs = r.logs || [];
      const notImpl = logs.every(l => l.status === 'not_implemented');
      const inserted = logs.reduce((n, l) => n + (l.imported || 0), 0);
      const updated  = logs.reduce((n, l) => n + (l.updated || 0), 0);
      const failed   = logs.reduce((n, l) => n + (l.failed || 0), 0);
      toast({
        title: notImpl ? 'Adapter aún no implementado' : 'Sincronización completa',
        description: notImpl
          ? 'Este proveedor aún no está listo. Wasi ya es funcional.'
          : `${inserted} nuevas · ${updated} actualizadas${failed ? ` · ${failed} con errores` : ''}`,
      });
      setSelectedProvider(slug);
      await load();
    } catch (err) { toast({ title: 'Error', description: friendlyError(err), variant: 'destructive' }); }
  };
  const onDisconnect = async (slug) => {
    try { await api.integrationsDisconnect(slug); toast({ title: `${slug} desconectado` }); await load(); }
    catch (err) { toast({ title: 'Error', description: friendlyError(err), variant: 'destructive' }); }
  };
  const onScheduleChange = async (slug, newSchedule) => {
    try {
      await api.integrationsUpdateSchedule(slug, newSchedule);
      toast({ title: 'Frecuencia actualizada', description: SCHEDULE_OPTIONS.find(o => o.value === newSchedule)?.label });
      await load();
    } catch (err) { toast({ title: 'Error', description: friendlyError(err), variant: 'destructive' }); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="integrations-center">
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <Link to="/crm" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-habita mb-3">
            <ArrowLeft className="w-4 h-4" /> Volver al CRM
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-habita" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-habita">Ecosistema Habita</span>
              </div>
              <h1 className="font-serif text-3xl font-bold text-habita-dark">Centro de Integraciones</h1>
              <p className="text-sm text-gray-500 mt-1 max-w-2xl">Conecta el CRM que ya usas. Habita se sincroniza con tus propiedades, proyectos y leads sin duplicar información.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-16 text-gray-400"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>
        ) : (
          <>
            {/* FASE 4B.2 — Universal Import Wizard CTA */}
            <div className="bg-gradient-to-br from-habita/10 via-emerald-50 to-white border border-habita/30 rounded-2xl p-5 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="import-wizard-cta">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-habita text-white flex items-center justify-center shrink-0">
                  <FileSpreadsheet className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-3.5 h-3.5 text-habita" />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-habita">Nuevo · FASE 4B.2</span>
                  </div>
                  <h3 className="font-serif text-xl font-bold text-habita-dark">Asistente de Migración Inteligente</h3>
                  <p className="text-sm text-gray-600 max-w-xl">Importa tu inventario desde Excel o CSV con auto-detección de columnas, dry-run y detección de duplicados. Mismo motor que usarán Wasi, Tokko e Inmovilla.</p>
                </div>
              </div>
              <Link
                to="/crm/integraciones/importar"
                className="h-11 px-6 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark whitespace-nowrap flex items-center gap-2"
                data-testid="launch-import-wizard"
              >
                Importar inventario <ArrowLeft className="w-4 h-4 rotate-180" />
              </Link>
            </div>

            <h2 className="font-serif text-xl font-bold text-habita-dark mb-4">CRMs disponibles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="providers-grid">
              {providers.map(p => (
                <ProviderCard
                  key={p.slug}
                  provider={p}
                  connection={byProvider[p.slug]}
                  onConnect={setShowConnect}
                  onSync={onSync}
                  onDisconnect={onDisconnect}
                  onScheduleChange={onScheduleChange}
                />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
              <div className="lg:col-span-2">
                <ApiKeysPanel />
              </div>
              <div>
                <SyncHistory provider={selectedProvider} agencyReady={!loading} />
              </div>
            </div>
          </>
        )}
      </div>

      <ConnectModal provider={showConnect} onClose={() => setShowConnect(null)} onConnected={load} />
    </div>
  );
}
